import { Box, VStack } from '@web/components/atoms/Layout'
import { useEffect, useMemo, useState } from 'react'
import { ComponentType, type ProgressBar as ProgressBarProps } from '@web/graphql-components'
import { useBrandingsStore } from '@web/components/utils/branding/useBrandingsStore'
import { getProgressBarDefaultStyles, ProgressBarType } from './progressBarUtils'
import { Text } from '../Text'
import { useIsHiddenFromDates } from '@web/components/utils/dateUtils'
import ProgressDisplay from './ProgressDisplay'
import { sum } from '@web/components/utils/format'
import { useMergeFields } from '../Text/MergeTags/MergeTag'
import { MATCHED_GIVING_MERGE_TAGS, PROGRESS_BAR_MERGE_TAGS } from '../Text/MergeTags/utils'
import { useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'
import { DESKTOP_CONTAINER_QUERY, MOBILE_CONTAINER_QUERY } from '../utils'

const [GOAL_TAG] = PROGRESS_BAR_MERGE_TAGS
const [MG_GOAL_TAG] = MATCHED_GIVING_MERGE_TAGS

const ProgressBar = ({
  id,
  goals,
  start,
  end,
  bonusGoals,
  hideOnGoalReached,
  noPreview,
  styles,
  mobileStyles,
  title,
  progress = 0,
  dateToUse,
}: ProgressBarProps & { noPreview?: boolean; progress?: number; dateToUse?: string | null }) => {
  const colorData = useBrandingsStore(({ colorData }) => colorData)
  const defaultStyles = useMemo(() => getProgressBarDefaultStyles(colorData), [colorData])

  const [currentGoal, setCurrentGoal] = useState(0)
  const progressBarTitle = useMemo(() => {
    if (currentGoal > 0 && bonusGoals) {
      return bonusGoals?.[currentGoal - 1].text?.textValue ?? title
    }

    return title
  }, [currentGoal])

  const { borderRadius = defaultStyles.borderRadius, textColor } = styles || {}
  const backgroundColor = useColorWithOpacity(
    styles?.backgroundColor,
    styles?.backgroundColorOpacity,
    defaultStyles.backgroundColor,
  )
  const barColor = useColorWithOpacity(styles?.barColor, styles?.barColorOpacity, defaultStyles.barColor)
  const progressColor = useColorWithOpacity(
    styles?.progressColor,
    styles?.progressColorOpacity,
    defaultStyles.progressColor,
  )

  const labelColor = textColor === 'light' ? colorData['Text light'] : colorData['Text dark']
  const isHidden = useIsHiddenFromDates(start, end, dateToUse) || (hideOnGoalReached && progress >= sum(goals ?? []))

  useEffect(() => {
    if (!goals) {
      return
    }
    let sum = 0
    for (let index = 0; index < goals.length; index++) {
      const goal = goals[index]
      sum += goal
      if (progress < sum) {
        break
      }
    }

    const goalTagToUse = styles?.options?.type === ProgressBarType.MatchedGiving ? MG_GOAL_TAG : GOAL_TAG

    useMergeFields
      .getState()
      .setMergeField(goalTagToUse.MergeFieldSettingId, sum.toLocaleString('en', { useGrouping: true }))
  }, [goals, progress])

  if (isHidden) {
    return null
  }

  return (
    <VStack
      id={`progress-bar-${id}`}
      align="center"
      py={2}
      w="100%"
      bgColor={backgroundColor}
      css={{
        [MOBILE_CONTAINER_QUERY]: {
          p: mobileStyles?.padding ?? 0,
        },
        [DESKTOP_CONTAINER_QUERY]: {
          p: styles?.padding ?? 0,
        },
      }}
      borderRadius={`${borderRadius}px`}
      color={labelColor}
    >
      {progressBarTitle && (
        <Box w="100%">
          <Text id="progress-bar-title" type={ComponentType.Text} textValue={progressBarTitle} />
        </Box>
      )}
      <ProgressDisplay
        type={styles?.options?.type}
        switchGoal={(index) => setCurrentGoal(index)}
        amountRaised={progress ?? 0}
        barColor={barColor}
        goals={goals ?? [0]}
        progressColor={progressColor}
        isAnimated={!noPreview}
        progressBarIcon={styles?.progressBarIcon}
      />
    </VStack>
  )
}

export { ProgressBar }
